import { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";

import apiService from "../../../service/Api";
import {
  Collection,
  CrewDocumentInput,
  CrewDocumentRecord,
  extractCollection,
} from "../types/crew-documents";

const DOCUMENTS_ENDPOINT = "/api/crews/document-certifications/";

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  if (!(error instanceof AxiosError)) return fallback;

  const data = error.response?.data as
    | Record<string, unknown>
    | string
    | undefined;

  if (typeof data === "string" && data.trim()) return data;
  if (!data || typeof data !== "object") return fallback;

  if (typeof data.message === "string") return data.message;
  if (typeof data.detail === "string") return data.detail;

  // DRF field errors, e.g. { document: ["The submitted file is empty."] }
  const firstFieldError = Object.values(data).find(
    (value) => Array.isArray(value) && typeof value[0] === "string",
  ) as string[] | undefined;

  return firstFieldError?.[0] ?? fallback;
};

export interface UseCrewDocuments {
  documents: CrewDocumentRecord[];
  loading: boolean;
  saving: boolean;
  refresh: () => Promise<void>;
  uploadDocument: (input: CrewDocumentInput) => Promise<boolean>;
  updateDocument: (
    record: CrewDocumentRecord,
    input: CrewDocumentInput,
  ) => Promise<boolean>;
  deleteDocument: (record: CrewDocumentRecord) => Promise<boolean>;
}

/**
 * Loads and mutates one crew member's documents & certifications.
 * Pass `undefined` while the crew member is still loading — the fetch runs once
 * the id arrives.
 */
export const useCrewDocuments = (crewMemberId?: number): UseCrewDocuments => {
  const [documents, setDocuments] = useState<CrewDocumentRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const fetchDocuments = useCallback(async () => {
    if (!crewMemberId) return;

    setLoading(true);
    try {
      const response = await apiService.get<Collection<CrewDocumentRecord>>(
        DOCUMENTS_ENDPOINT,
        { crew_member: crewMemberId },
      );
      setDocuments(extractCollection(response.data));
    } catch (error) {
      // The list 404s when the crew member has nothing on file — that's an
      // empty list, not an error worth surfacing.
      if (error instanceof AxiosError && error.response?.status === 404) {
        setDocuments([]);
      } else {
        console.error("Failed to fetch crew documents", error);
        toast.error(
          resolveErrorMessage(error, "Failed to load documents. Please try again."),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [crewMemberId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const buildFormData = useCallback(
    ({ typeName, file }: CrewDocumentInput) => {
      const formData = new FormData();
      formData.append("type_name", typeName);
      formData.append("crew_member", String(crewMemberId));
      // Only appended when a new file was picked, so a category rename keeps
      // the stored file untouched.
      if (file) formData.append("document", file);
      return formData;
    },
    [crewMemberId],
  );

  const uploadDocument = useCallback(
    async ({ typeName, file }: CrewDocumentInput) => {
      if (!crewMemberId || !file) return false;

      setSaving(true);
      try {
        const response = await apiService.post<CrewDocumentRecord>(
          DOCUMENTS_ENDPOINT,
          buildFormData({ typeName, file }),
        );
        setDocuments((current) => [...current, response.data]);
        toast.success(`${typeName} uploaded successfully.`);
        return true;
      } catch (error) {
        console.error("Failed to upload crew document", error);
        toast.error(
          resolveErrorMessage(error, "Failed to upload the document. Please try again."),
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [buildFormData, crewMemberId],
  );

  const updateDocument = useCallback(
    async (record: CrewDocumentRecord, input: CrewDocumentInput) => {
      if (!crewMemberId) return false;

      setSaving(true);
      try {
        const response = await apiService.patch<CrewDocumentRecord>(
          `${DOCUMENTS_ENDPOINT}${record.id}/`,
          buildFormData(input),
        );
        setDocuments((current) =>
          current.map((item) => (item.id === record.id ? response.data : item)),
        );
        toast.success("Document updated successfully.");
        return true;
      } catch (error) {
        console.error("Failed to update crew document", error);
        toast.error(
          resolveErrorMessage(error, "Failed to update the document. Please try again."),
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [buildFormData, crewMemberId],
  );

  const deleteDocument = useCallback(async (record: CrewDocumentRecord) => {
    setSaving(true);
    try {
      await apiService.delete(`${DOCUMENTS_ENDPOINT}${record.id}/`);
      setDocuments((current) => current.filter((item) => item.id !== record.id));
      toast.success("Document deleted successfully.");
      return true;
    } catch (error) {
      console.error("Failed to delete crew document", error);
      toast.error(
        resolveErrorMessage(error, "Failed to delete the document. Please try again."),
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    documents,
    loading,
    saving,
    refresh: fetchDocuments,
    uploadDocument,
    updateDocument,
    deleteDocument,
  };
};

export default useCrewDocuments;
